import React, { useState, useEffect } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Snackbar, Alert, InputAdornment, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import api, { apiService } from "../../utils/api";

const PlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    duration: "",
    features: "",
    description: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await apiService.plans();
      setPlans(response.data.data);
    } catch (error) {
      showSnackbar("Error fetching plans", "error");
    }
  };

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        price: plan.price,
        duration: plan.duration,
        features: plan.features.join("\n"),
        description: plan.description,
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: "",
        price: "",
        duration: "",
        features: "",
        description: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPlan(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const planData = {
        ...formData,
        features: formData.features.split("\n").filter((feature) => feature.trim()),
      };

      if (editingPlan) {
        await apiService.planEdit(`${editingPlan.id}`, planData);
        showSnackbar("Plan updated successfully");
      } else {
        await apiService.planAdd(planData);
        showSnackbar("Plan created successfully");
      }

      handleCloseDialog();
      fetchPlans();
    } catch (error) {
      showSnackbar("Error saving plan", "error");
    }
  };

  const handleDelete = async (planId) => {
    if (window.confirm("Are you sure you want to delete this plan?")) {
      try {
        await apiService.planDelete(`${planId}`);
        showSnackbar("Plan deleted successfully");
        fetchPlans();
      } catch (error) {
        showSnackbar("Error deleting plan", "error");
      }
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" component="h1">
          Plan Management
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
          Add New Plan
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan) => (
              <TableRow key={plan.id}>
                <TableCell>{plan.name}</TableCell>
                <TableCell>{plan.price}</TableCell>
                <TableCell>{plan.duration}</TableCell>
                <TableCell>{plan.features?.join(",")}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleOpenDialog(plan)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(plan.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPlan ? "Edit Plan" : "Create New Plan"}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField fullWidth label="Plan Name" name="name" value={formData.name} onChange={handleInputChange} margin="normal" required />
            <TextField fullWidth label="Description" name="description" value={formData.description} onChange={handleInputChange} margin="normal" multiline rows={3} />
            <TextField fullWidth label="Price" name="price" type="number" value={formData.price} onChange={handleInputChange} margin="normal" required InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Duration</InputLabel>
              <Select name="duration" value={formData.duration} onChange={handleInputChange} label="Duration">
                {months.map((month) => {
                  return <MenuItem value={month}>{month} Month</MenuItem>;
                })}
              </Select>
            </FormControl>
            <TextField fullWidth label="Features (one per line)" name="features" value={formData.features} onChange={handleInputChange} margin="normal" multiline rows={4} required />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingPlan ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanManagement;
